/**
 * GameRegister.tsx
 *
 * MakeCode Arcade 게임 등록 및 관리 페이지
 *
 * 주요 기능:
 * - 새로운 게임 등록 (제목, 설명, URL, 시뮬레이터 코드 등)
 * - 게임 아이콘 이미지 업로드 및 미리보기
 * - 기존 게임 수정 및 삭제
 * - 평가용 게임 등록 (평가용 코드 검증)
 * - 학년/반 선택 기능
 * - 내가 등록한 게임 목록 표시
 * - 학교 정보 연동
 *
 * 사용되는 곳:
 * - /makecodearcade/register 경로
 * - 게임 개발자가 자신의 게임을 등록하고 관리하는 페이지
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import {
  GAME_EDIT_SECRET_COLLECTION,
  GAME_UPLOAD_CODE_COLLECTION,
  createGuestEditToken,
  createGuestGameId,
  getGuestUploaderId,
  hashGuestEditToken,
  normalizeTeacherCode,
  readGuestEditToken,
  readGuestGameIds,
  rememberGuestEditToken,
  rememberGuestGameId,
  submitGuestGameEdit,
} from '../../utils/arcadeGuestUpload';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import ArcadeGamePoster from './ArcadeGamePoster';

/**
 * 게임 데이터 인터페이스
 */
interface Game {
  id: string;
  title: string;
  description: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  schoolName: string;
  schoolId: string;
  userId: string;
  createdAt: Date;
  rating: number;
  playCount: number;
  iconUrl?: string;
  introduction?: string;
  isEvaluationGame?: boolean;
  evaluationCode?: string;
  evaluationInfo?: {
    schoolId: string;
    schoolName: string;
    grade: number;
    startDate: any;
    endDate: any;
    reviewerClasses: { [key: string]: string };
  };
  grade?: number;
  class?: string;
  commentsOnly?: boolean;
  allowGuestComments?: boolean;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #F0F0F0',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'Pretendard, sans-serif',
  boxSizing: 'border-box',
  backgroundColor: '#FFFFFF',
  color: '#161616',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 500,
  color: '#161616',
  marginBottom: '8px'
};

const helperTextStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#989898',
  marginTop: '4px'
};

const GameRegister: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useLayoutContext();

  const [loading, setLoading] = useState(false);
  const [myGames, setMyGames] = useState<Game[]>([]);
  const [schoolName, setSchoolName] = useState<string>('');
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<any>(null);
  const [loadingSchoolInfo, setLoadingSchoolInfo] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    gameUrl: '',
    simulatorCode: '',
    processUrl: '',
    companyName: '',
    introduction: '',
    grade: '',
    class: '',
    creatorName: ''
  });

  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const [isEvaluationGame, setIsEvaluationGame] = useState(false);
  const [evaluationCode, setEvaluationCode] = useState('');
  const [evaluationInfo, setEvaluationInfo] = useState<Game['evaluationInfo'] | null>(null);
  const [evaluationCodeError, setEvaluationCodeError] = useState('');
  const [verifiedLookup, setVerifiedLookup] = useState<Record<string, any> | null>(null);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [allowGuestComments, setAllowGuestComments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [titleFilter, setTitleFilter] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [gameTypeFilter, setGameTypeFilter] = useState<'all' | 'evaluation' | 'regular'>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'title' | 'rating' | 'playCount'>('createdAt');

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      setLoadingSchoolInfo(false);
      fetchGuestGames();
      return;
    }
    fetchMyGames();
    fetchSchoolName();
  }, [currentUser, authLoading]);

  const fetchMyGames = async () => {
    if (!currentUser) return;
    try {
      const gamesRef = collection(db, 'games');
      const q = query(gamesRef, where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const gamesList = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt.toDate()
      })) as Game[];
      setMyGames(gamesList);
    } catch (err) {
      console.error('Error fetching my games:', err);
    }
  };

  const fetchGuestGames = async () => {
    const ids = readGuestGameIds();
    if (ids.length === 0) {
      setMyGames([]);
      return;
    }
    try {
      const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'games', id))));
      const gamesList = snaps
        .filter((snap) => snap.exists())
        .map((snap) => ({
          id: snap.id,
          ...snap.data(),
          createdAt: snap.data().createdAt?.toDate?.() || new Date(),
        })) as Game[];
      setMyGames(gamesList);
    } catch (err) {
      console.error('Error fetching guest games:', err);
    }
  };

  const fetchSchoolName = async () => {
    if (!currentUser) {
      setLoadingSchoolInfo(false);
      return;
    }
    try {
      setLoadingSchoolInfo(true);
      let schoolId = currentUser.schoolId;
      if (!schoolId && 'schoolId' in currentUser && currentUser.schoolId) {
        schoolId = currentUser.schoolId;
      }
      if (!schoolId && currentUser.uid) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) schoolId = userDoc.data().schoolId;
      }
      if (!schoolId) {
        setSchoolName('미지정');
        setSchoolInfo(null);
        setLoadingSchoolInfo(false);
        return;
      }
      const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
      if (schoolDoc.exists()) {
        const schoolData = schoolDoc.data();
        setSchoolName(schoolData.name || '미지정');
        setSchoolInfo(schoolData);
      } else {
        setSchoolName('미지정');
        setSchoolInfo(null);
      }
    } catch (err) {
      console.error('Error fetching school name:', err);
      setSchoolName('미지정');
      setSchoolInfo(null);
    } finally {
      setLoadingSchoolInfo(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        toast.error('파일 크기는 5MB를 초과할 수 없습니다.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('이미지 파일만 업로드 가능합니다.');
        return;
      }
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setIconPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleEvaluationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEvaluationCode(e.target.value);
    setEvaluationCodeError('');
    setVerifiedLookup(null);
    setEvaluationInfo(null);
  };

  const checkEvaluationCode = async () => {
    if (!evaluationCode.trim()) {
      setEvaluationCodeError(currentUser ? '평가용 코드를 입력해주세요.' : '코드를 입력해 주세요.');
      return;
    }
    setIsCheckingCode(true);
    setEvaluationCodeError('');
    setVerifiedLookup(null);
    try {
      if (!currentUser) {
        const normalized = normalizeTeacherCode(evaluationCode);
        const snap = await getDoc(doc(db, GAME_UPLOAD_CODE_COLLECTION, normalized));
        if (!snap.exists()) {
          setEvaluationCodeError('코드를 확인할 수 없습니다. 선생님께 등록 코드를 다시 확인해 주세요.');
          setEvaluationInfo(null);
          return;
        }
        const codeData = snap.data();
        setVerifiedLookup(codeData);
        setEvaluationCode(normalized);
        setSchoolName(codeData.schoolName || '');
        setEvaluationInfo(null);
        setEvaluationCodeError('');
        return;
      }
      const evaluationCodesRef = collection(db, 'evaluationCodes');
      const q = query(evaluationCodesRef, where('code', '==', evaluationCode));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        setEvaluationCodeError('유효하지 않은 평가용 코드입니다.');
        setEvaluationInfo(null);
      } else {
        const codeData = querySnapshot.docs[0].data();
        const parsedStartDate = codeData.startDate?.toDate?.() ?? (codeData.startDate instanceof Date ? codeData.startDate : new Date(codeData.startDate));
        const parsedEndDate = codeData.endDate?.toDate?.() ?? (codeData.endDate instanceof Date ? codeData.endDate : new Date(codeData.endDate));
        if (isNaN(parsedStartDate.getTime()) || isNaN(parsedEndDate.getTime())) {
          setEvaluationCodeError('평가 기간 정보가 올바르지 않습니다.');
          setEvaluationInfo(null);
          return;
        }
        setEvaluationInfo({
          schoolId: codeData.schoolId,
          schoolName: codeData.schoolName,
          grade: codeData.grade,
          startDate: parsedStartDate,
          endDate: parsedEndDate,
          reviewerClasses: codeData.reviewerClasses || {}
        });
        setEvaluationCodeError('');
      }
    } catch (err) {
      console.error('Error checking evaluation code:', err);
      setEvaluationCodeError(currentUser
        ? '평가용 코드 확인 중 오류가 발생했습니다.'
        : '코드를 확인하지 못했습니다. 코드가 맞는지 다시 확인해 주세요.');
      setEvaluationInfo(null);
      setVerifiedLookup(null);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isGuest = !currentUser;
    if (isGuest && editingGameId) {
      const editToken = readGuestEditToken(editingGameId);
      if (!editToken) {
        toast.error('이 게임을 등록한 브라우저에만 수정 키가 있습니다. 이 브라우저에서는 수정할 수 없습니다.');
        return;
      }
      if (!formData.creatorName.trim()) {
        toast.error('제작자 이름을 입력해 주세요.');
        return;
      }
      if (!formData.title || !formData.gameUrl || !formData.simulatorCode) {
        toast.error('게임 제목, 게임 URL, 시뮬레이터 코드는 필수입니다.');
        return;
      }
      setLoading(true);
      try {
        await submitGuestGameEdit(editingGameId, editToken, {
          title: formData.title,
          description: formData.description,
          introduction: formData.introduction,
          gameUrl: formData.gameUrl,
          simulatorCode: formData.simulatorCode,
          processUrl: formData.processUrl,
          companyName: formData.companyName,
          creatorName: formData.creatorName,
          grade: formData.grade ? parseInt(formData.grade, 10) : null,
          class: formData.class ? parseInt(formData.class, 10) : null,
          commentsOnly,
          allowGuestComments,
        });
        toast.success('게임이 수정되었습니다.');
        setEditingGameId(null);
        setFormData({
          title: '',
          description: '',
          gameUrl: '',
          simulatorCode: '',
          processUrl: '',
          companyName: '',
          introduction: '',
          grade: '',
          class: '',
          creatorName: ''
        });
        setIconFile(null);
        setIconPreview(null);
        setIsEvaluationGame(false);
        setEvaluationCode('');
        setEvaluationInfo(null);
        setVerifiedLookup(null);
        setCommentsOnly(false);
        setAllowGuestComments(false);
        fetchGuestGames();
      } catch (err) {
        console.error('Error updating guest game:', err);
        toast.error(err instanceof Error ? err.message : '게임을 수정하지 못했습니다.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (isGuest && !verifiedLookup) {
      toast.error('선생님이 알려 준 코드를 확인한 뒤 등록해 주세요.');
      return;
    }
    if (isGuest && !formData.creatorName.trim()) {
      toast.error('제작자 이름을 입력해 주세요.');
      return;
    }
    if (!isGuest && !currentUser) {
      toast.error('로그인이 필요합니다.');
      return;
    }
    if (!formData.title || !formData.gameUrl || !formData.simulatorCode) {
      toast.error('게임 제목, 게임 URL, 시뮬레이터 코드는 필수입니다.');
      return;
    }
    if (!isGuest && isEvaluationGame && !evaluationInfo) {
      toast.error('평가용 게임을 등록하려면 유효한 평가용 코드가 필요합니다.');
      return;
    }
    setLoading(true);
    try {
      let iconUrl = '';
      if (!isGuest && iconFile && storage && currentUser) {
        setUploadingIcon(true);
        const storageRef = ref(storage, `game-icons/${currentUser.uid}/${Date.now()}_${iconFile.name}`);
        await uploadBytes(storageRef, iconFile);
        iconUrl = await getDownloadURL(storageRef);
        setUploadingIcon(false);
      }
      const gameData: any = {
        title: formData.title,
        description: formData.description,
        gameUrl: formData.gameUrl,
        simulatorCode: formData.simulatorCode,
        processUrl: formData.processUrl,
        companyName: formData.companyName,
        creatorName: isGuest ? formData.creatorName.trim() : (currentUser?.name || '익명'),
        schoolName: isGuest ? (verifiedLookup?.schoolName || schoolName) : schoolName,
        userId: isGuest ? getGuestUploaderId() : currentUser?.uid,
        introduction: formData.introduction,
        grade: formData.grade ? parseInt(formData.grade) : null,
        class: formData.class ? parseInt(formData.class) : null
      };
      gameData.schoolId = isGuest
        ? (verifiedLookup?.schoolId || '')
        : (currentUser?.schoolId || schoolInfo?.id || '');
      if (iconUrl) gameData.iconUrl = iconUrl;
      if (isGuest && verifiedLookup) {
        gameData.guestUpload = true;
        gameData.isEvaluationGame = false;
        gameData.uploadCode = normalizeTeacherCode(evaluationCode);
        gameData.evaluationCode = null;
        gameData.evaluationInfo = null;
        gameData.createdAt = Timestamp.fromDate(new Date());
        gameData.rating = 0;
        gameData.playCount = 0;
        gameData.commentsOnly = commentsOnly;
        gameData.allowGuestComments = allowGuestComments;
        const gameId = createGuestGameId();
        const editToken = createGuestEditToken();
        let secretSaved = false;
        try {
          const tokenHash = await hashGuestEditToken(gameId, editToken);
          await setDoc(doc(db, GAME_EDIT_SECRET_COLLECTION, gameId), {
            tokenHash,
            createdAt: serverTimestamp(),
          });
          secretSaved = true;
        } catch (secretError) {
          console.error('게스트 수정 키 저장 실패:', secretError);
        }
        await setDoc(doc(db, 'games', gameId), gameData);
        rememberGuestGameId(gameId);
        if (secretSaved) {
          rememberGuestEditToken(gameId, editToken);
          toast.success('게임이 등록되었습니다.');
        } else {
          toast.success('게임이 등록되었습니다. 수정 키를 저장하지 못해, 내용은 코드를 발급한 선생님이 수정할 수 있습니다.');
        }
        setFormData({
          title: '',
          description: '',
          gameUrl: '',
          simulatorCode: '',
          processUrl: '',
          companyName: '',
          introduction: '',
          grade: '',
          class: '',
          creatorName: ''
        });
        setIconFile(null);
        setIconPreview(null);
        setEvaluationCode('');
        setEvaluationInfo(null);
        setVerifiedLookup(null);
        setCommentsOnly(false);
        setAllowGuestComments(false);
        fetchGuestGames();
        return;
      }
      if (isEvaluationGame && evaluationInfo) {
        gameData.isEvaluationGame = true;
        gameData.evaluationCode = evaluationCode;
        gameData.evaluationInfo = {
          schoolId: evaluationInfo.schoolId,
          schoolName: evaluationInfo.schoolName,
          grade: evaluationInfo.grade,
          startDate: evaluationInfo.startDate instanceof Date ? Timestamp.fromDate(evaluationInfo.startDate) : evaluationInfo.startDate,
          endDate: evaluationInfo.endDate instanceof Date ? Timestamp.fromDate(evaluationInfo.endDate) : evaluationInfo.endDate,
          reviewerClasses: evaluationInfo.reviewerClasses
        };
      } else {
        gameData.isEvaluationGame = false;
        gameData.evaluationCode = null;
        gameData.evaluationInfo = null;
      }
      gameData.commentsOnly = commentsOnly;
      gameData.allowGuestComments = allowGuestComments;
      if (editingGameId) {
        await updateDoc(doc(db, 'games', editingGameId), gameData);
        toast.success('게임이 성공적으로 수정되었습니다!');
        setEditingGameId(null);
      } else {
        gameData.createdAt = new Date();
        gameData.rating = 0;
        gameData.playCount = 0;
        await addDoc(collection(db, 'games'), gameData);
        toast.success('게임이 성공적으로 등록되었습니다!');
      }
      setFormData({
        title: '',
        description: '',
        gameUrl: '',
        simulatorCode: '',
        processUrl: '',
        companyName: '',
        introduction: '',
        grade: '',
        class: '',
        creatorName: ''
      });
      setIconFile(null);
      setIconPreview(null);
      setIsEvaluationGame(false);
      setEvaluationCode('');
      setEvaluationInfo(null);
      setCommentsOnly(false);
      setAllowGuestComments(false);
      fetchMyGames();
    } catch (err) {
      console.error('Error registering game:', err);
      toast.error('게임 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (game: Game) => {
    if (!currentUser && !readGuestEditToken(game.id)) {
      toast.error('이 게임을 등록한 브라우저에만 수정 키가 있습니다. 이 브라우저에서는 수정할 수 없습니다.');
      return;
    }
    if (isMyGamesView) {
      navigate('/makecodearcade/register');
    }
    setEditingGameId(game.id);
    setFormData({
      title: game.title,
      description: game.description,
      gameUrl: game.gameUrl,
      simulatorCode: game.simulatorCode,
      processUrl: game.processUrl,
      companyName: game.companyName,
      introduction: game.introduction || '',
      grade: game.grade?.toString() || '',
      class: game.class?.toString() || '',
      creatorName: game.creatorName || ''
    });
    setIconPreview(game.iconUrl || null);
    if (game.isEvaluationGame) {
      setIsEvaluationGame(true);
      setEvaluationCode(game.evaluationCode || '');
      if (game.evaluationInfo) {
        const startDate = game.evaluationInfo.startDate?.toDate?.() ?? (game.evaluationInfo.startDate instanceof Date ? game.evaluationInfo.startDate : new Date(game.evaluationInfo.startDate));
        const endDate = game.evaluationInfo.endDate?.toDate?.() ?? (game.evaluationInfo.endDate instanceof Date ? game.evaluationInfo.endDate : new Date(game.evaluationInfo.endDate));
        setEvaluationInfo({ ...game.evaluationInfo, startDate, endDate });
      } else setEvaluationInfo(null);
    } else {
      setIsEvaluationGame(false);
      setEvaluationCode('');
      setEvaluationInfo(null);
    }
    setCommentsOnly(Boolean(game.commentsOnly));
    setAllowGuestComments(Boolean(game.allowGuestComments));
    if (!currentUser) {
      setIsEvaluationGame(false);
      setEvaluationInfo(null);
    }
  };

  const handleDelete = async (gameId: string) => {
    if (!window.confirm('정말로 이 게임을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'games', gameId));
      toast.success('게임이 삭제되었습니다.');
      fetchMyGames();
    } catch (err) {
      console.error('Error deleting game:', err);
      toast.error('게임 삭제 중 오류가 발생했습니다.');
    }
  };

  const formatDate = (d: any) => {
    if (!d) return '-';
    const date = d?.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString();
  };

  const filterSchools = useMemo(() => {
    const schoolSet = new Set<string>();
    myGames.forEach(game => {
      if (game.schoolName) schoolSet.add(game.schoolName);
    });
    return Array.from(schoolSet).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [myGames]);

  const filterGrades = useMemo(() => {
    const gradeSet = new Set<number>();
    myGames.forEach(game => {
      if (schoolFilter && game.schoolName !== schoolFilter) return;
      if (game.grade) gradeSet.add(game.grade);
    });
    return Array.from(gradeSet).sort((a, b) => a - b);
  }, [myGames, schoolFilter]);

  const filterClasses = useMemo(() => {
    const classSet = new Set<number>();
    myGames.forEach(game => {
      if (schoolFilter && game.schoolName !== schoolFilter) return;
      if (gradeFilter && game.grade !== parseInt(gradeFilter)) return;
      if (game.class) classSet.add(game.class);
    });
    return Array.from(classSet).sort((a, b) => a - b);
  }, [myGames, schoolFilter, gradeFilter]);

  const filteredMyGames = useMemo(() => {
    let filtered = [...myGames];

    if (titleFilter.trim()) {
      const q = titleFilter.toLowerCase().trim();
      filtered = filtered.filter(game =>
        game.title.toLowerCase().includes(q) ||
        (game.description || '').toLowerCase().includes(q)
      );
    }

    if (schoolFilter) {
      filtered = filtered.filter(game => game.schoolName === schoolFilter);
    }

    if (gradeFilter) {
      filtered = filtered.filter(game => game.grade === parseInt(gradeFilter));
    }

    if (classFilter) {
      filtered = filtered.filter(game => game.class === parseInt(classFilter));
    }

    if (gameTypeFilter === 'evaluation') {
      filtered = filtered.filter(game => game.isEvaluationGame);
    } else if (gameTypeFilter === 'regular') {
      filtered = filtered.filter(game => !game.isEvaluationGame);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title, 'ko');
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'playCount':
          return (b.playCount || 0) - (a.playCount || 0);
        case 'createdAt':
        default:
          return b.createdAt.getTime() - a.createdAt.getTime();
      }
    });

    return filtered;
  }, [myGames, titleFilter, schoolFilter, gradeFilter, classFilter, gameTypeFilter, sortBy]);

  const hasActiveFilters = Boolean(
    titleFilter.trim() || schoolFilter || gradeFilter || classFilter || gameTypeFilter !== 'all' || sortBy !== 'createdAt'
  );

  const handleFilterReset = () => {
    setTitleFilter('');
    setSchoolFilter('');
    setGradeFilter('');
    setClassFilter('');
    setGameTypeFilter('all');
    setSortBy('createdAt');
  };

  const isMyGamesView = location.pathname.endsWith('/my-games');

  const registerTabs = [
    { id: 'register-new', label: '게임 등록', path: '/makecodearcade/register' },
    { id: 'my-games', label: '내가 등록한 게임', path: '/makecodearcade/register/my-games' },
    { id: 'register-guide', label: '등록 가이드', path: '/makecodearcade/register-guide' },
  ] as const;

  const gradeOptions = loadingSchoolInfo ? [] : schoolInfo?.gradeClasses
    ? Object.keys(schoolInfo.gradeClasses)
    : Array.from({ length: 6 }, (_, i) => (i + 1).toString());
  const classOptions = loadingSchoolInfo ? [] : schoolInfo?.gradeClasses && formData.grade
    ? Array.from({ length: schoolInfo.gradeClasses[parseInt(formData.grade)] || 10 }, (_, i) => (i + 1).toString())
    : schoolInfo?.gradeClasses
      ? Array.from({ length: Math.max(...Object.values(schoolInfo.gradeClasses || {}).map((v: any) => Number(v) || 0)) || 10 }, (_, i) => (i + 1).toString())
      : Array.from({ length: 10 }, (_, i) => (i + 1).toString());

  if (authLoading) {
    return (
      <MainLayout>
        <div style={{ ...getMakecodeArcadePageRootStyle(isMobile), textAlign: 'center' }}>불러오는 중...</div>
      </MainLayout>
    );
  }

  const isGuest = !currentUser;

  return (
    <MainLayout>
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
          <>
            <div style={{ fontSize: '24px', fontWeight: 600, marginBottom: 16 }}>
              {isMyGamesView ? '내가 등록한 게임' : (editingGameId ? '게임 수정' : '게임 등록')}
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              flexWrap: 'wrap',
            }}>
              {registerTabs.map((tab) => {
                const isActive = tab.id === 'my-games'
                  ? isMyGamesView
                  : tab.id === 'register-new' && !isMyGamesView;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => navigate(tab.path)}
                    style={{
                      padding: '10px 20px',
                      border: isActive ? '1.2px solid #FF6F0F' : '1.2px solid #2E2E3C',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#3A2418' : '#1C1C28',
                      color: isActive ? '#FF6F0F' : '#F4F4F8',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {!isMyGamesView && (
            <div style={{ background: 'var(--Color-background-normal, white)', borderRadius: '12px', padding: 24, marginBottom: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#161616' }}>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {isGuest && editingGameId && (
                    <div style={{ padding: 16, background: '#FFF4EC', borderRadius: 8, fontSize: 14, lineHeight: 1.5, color: '#161616' }}>
                      이 브라우저에서 코드로 등록한 게임을 수정합니다. 평가용 게임으로는 바뀌지 않습니다. 아이콘은 로그인한 뒤에만 올릴 수 있습니다.
                    </div>
                  )}
                  {isGuest && !editingGameId && (
                    <div style={{ padding: 16, background: '#FFF4EC', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>선생님이 알려 준 등록 코드</div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 12, lineHeight: 1.5 }}>
                        회원가입 없이, 등록 코드만 확인되면 자신의 게임을 올릴 수 있습니다.{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/makecodearcade/register-guide')}
                          style={{
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            color: '#FF6F0F',
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontFamily: 'Pretendard',
                          }}
                        >
                          등록 가이드
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <input
                          value={evaluationCode}
                          onChange={handleEvaluationCodeChange}
                          placeholder="등록 코드"
                          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                        />
                        <Button variant="primary" type="button" onClick={checkEvaluationCode} disabled={isCheckingCode}>
                          {isCheckingCode ? '확인 중...' : '코드 확인'}
                        </Button>
                      </div>
                      {evaluationCodeError && <div style={{ color: '#C33333', fontSize: 12, marginTop: 8 }}>{evaluationCodeError}</div>}
                      {verifiedLookup && (
                        <div style={{ marginTop: 12, fontSize: 14 }}>
                          소속: {verifiedLookup.schoolName || schoolName}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <img
                      src={iconPreview || '/images/icons/Logo.svg'}
                      alt="게임 아이콘"
                      style={{ width: 100, height: 100, borderRadius: '8px', objectFit: 'cover', background: '#F5F5F5' }}
                    />
                    <div>
                      <input ref={fileInputRef} accept="image/*" type="file" onChange={handleIconChange} style={{ display: 'none' }} />
                      <Button variant="outline" type="button" disabled={uploadingIcon || isGuest} onClick={() => fileInputRef.current?.click()}>
                        {uploadingIcon ? '업로드 중...' : '게임 아이콘 업로드'}
                      </Button>
                      {isGuest && <div style={helperTextStyle}>아이콘은 로그인한 뒤에 올릴 수 있습니다.</div>}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>게임 제목 *</label>
                    <input name="title" value={formData.title} onChange={handleChange} required style={inputStyle} placeholder="게임 제목을 입력하세요" />
                  </div>
                  <div>
                    <label style={labelStyle}>게임 설명</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} rows={4} style={{ ...inputStyle, minHeight: 80 }} placeholder="게임 설명을 입력하세요" />
                  </div>
                  <div>
                    <label style={labelStyle}>게임 소개 문구</label>
                    <textarea name="introduction" value={formData.introduction} onChange={handleChange} rows={3} style={{ ...inputStyle, minHeight: 60 }} placeholder="게임의 특징과 재미있는 점을 간단히 소개해주세요" />
                    <div style={helperTextStyle}>게임의 특징과 재미있는 점을 간단히 소개해주세요. 이 문구는 게임 상세 페이지에 표시됩니다.</div>
                  </div>
                  <div>
                    <label style={labelStyle}>게임 URL *</label>
                    <input name="gameUrl" value={formData.gameUrl} onChange={handleChange} required style={inputStyle} placeholder="https://makecode.com/_..." />
                    <div style={helperTextStyle}>예: https://makecode.com/_MH2WAy9ieYgU</div>
                  </div>
                  <div>
                    <label style={labelStyle}>시뮬레이터 코드 *</label>
                    <textarea name="simulatorCode" value={formData.simulatorCode} onChange={handleChange} required rows={4} style={{ ...inputStyle, minHeight: 80 }} placeholder="시뮬레이터 임베드 코드" />
                    <div style={helperTextStyle}>메이크코드 아케이드에서 제공하는 시뮬레이터 임베드 코드를 입력하세요</div>
                  </div>
                  <div>
                    <label style={labelStyle}>제작 과정 페이지 URL</label>
                    <input name="processUrl" value={formData.processUrl} onChange={handleChange} style={inputStyle} placeholder="제작 과정 페이지 URL (선택사항)" />
                  </div>
                  <div>
                    <label style={labelStyle}>제작 회사명</label>
                    <input name="companyName" value={formData.companyName} onChange={handleChange} style={inputStyle} placeholder="회사 또는 팀 이름 (선택사항)" />
                  </div>
                  {isGuest && (
                    <div>
                      <label style={labelStyle}>제작자 이름 *</label>
                      <input name="creatorName" value={formData.creatorName} onChange={handleChange} required style={inputStyle} placeholder="이름을 입력하세요" />
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <label style={labelStyle}>학년 (선택사항)</label>
                      <select name="grade" value={formData.grade} onChange={handleSelectChange} disabled={loadingSchoolInfo} style={inputStyle}>
                        <option value="">선택하지 않음</option>
                        {loadingSchoolInfo && <option disabled>로딩 중...</option>}
                        {gradeOptions.map((g) => (
                          <option key={g} value={g}>{g}학년</option>
                        ))}
                      </select>
                      {!isGuest && !loadingSchoolInfo && !schoolInfo?.gradeClasses && <div style={helperTextStyle}>학교 정보가 없어 기본값(1-6학년)을 표시합니다.</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <label style={labelStyle}>반 (선택사항)</label>
                      <select name="class" value={formData.class} onChange={handleSelectChange} disabled={loadingSchoolInfo} style={inputStyle}>
                        <option value="">선택하지 않음</option>
                        {loadingSchoolInfo && <option disabled>로딩 중...</option>}
                        {classOptions.map((c) => (
                          <option key={c} value={c}>{c}반</option>
                        ))}
                      </select>
                      {!isGuest && !loadingSchoolInfo && !schoolInfo?.gradeClasses && <div style={helperTextStyle}>학교 정보가 없어 기본값(1-10반)을 표시합니다.</div>}
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid #F0F0F0', margin: '16px 0', paddingTop: 16 }} />

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={commentsOnly} onChange={(e) => setCommentsOnly(e.target.checked)} />
                    <span>별점 없이 댓글만 받기</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={allowGuestComments}
                      onChange={(e) => setAllowGuestComments(e.target.checked)}
                    />
                    <span>로그인 없이 별점·리뷰 등록 허용</span>
                  </label>
                  <div style={helperTextStyle}>
                    비로그인 허용 시 같은 브라우저에서 게임당 1회만 등록할 수 있습니다. 댓글만 받기를 켜면 별점 없이 텍스트만, 끄면 별점과 리뷰를 함께 등록할 수 있습니다.
                  </div>

                  {!isGuest && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={isEvaluationGame} onChange={(e) => setIsEvaluationGame(e.target.checked)} />
                    <span>평가용 게임으로 등록</span>
                  </label>
                  )}

                  {!isGuest && isEvaluationGame && (
                    <div style={{ padding: 16, background: '#F8F8F8', borderRadius: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 18 }}>ℹ️</span>
                        <span style={{ fontWeight: 600 }}>평가용 게임 등록</span>
                      </div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                        평가용 게임으로 등록하면 지정된 학급의 학생들만 리뷰와 별점을 남길 수 있습니다. 교사가 제공한 평가용 코드를 입력해주세요.
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                        <input
                          value={evaluationCode}
                          onChange={handleEvaluationCodeChange}
                          placeholder="평가용 코드"
                          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
                        />
                        <Button variant="primary" onClick={checkEvaluationCode} disabled={isCheckingCode}>
                          {isCheckingCode ? '확인 중...' : '코드 확인'}
                        </Button>
                      </div>
                      {evaluationCodeError && <div style={{ color: '#C33333', fontSize: 12, marginBottom: 8 }}>{evaluationCodeError}</div>}
                      {evaluationInfo && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontWeight: 600, marginBottom: 8 }}>평가 정보</div>
                          <div style={{ fontSize: 14 }}>학교: {evaluationInfo.schoolName}</div>
                          <div style={{ fontSize: 14 }}>학년: {evaluationInfo.grade}학년</div>
                          <div style={{ fontSize: 14 }}>평가 기간: {formatDate(evaluationInfo.startDate)} ~ {formatDate(evaluationInfo.endDate)}</div>
                          <div style={{ fontSize: 14 }}>평가 대상: {Object.entries(evaluationInfo.reviewerClasses).map(([r, t]) => `${r}반 → ${t}반`).join(', ')}</div>
                        </div>
                      )}
                    </div>
                  )}

                  <Button type="submit" variant="submit" disabled={loading || (isGuest && !editingGameId && !verifiedLookup)} style={{ marginTop: 8 }}>
                    {loading ? '처리 중...' : (editingGameId ? '게임 수정' : '게임 등록')}
                  </Button>
                </div>
              </form>
            </div>
            )}

            {isMyGamesView && (
            <>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: 16, color: '#C8C8D4' }}>
              총 {filteredMyGames.length}개
            </div>
            {isGuest && (
              <div style={{ fontSize: 13, color: '#C8C8D4', marginTop: -8, marginBottom: 16, lineHeight: 1.5 }}>
                이 브라우저에서 코드로 등록한 게임은 수정할 수 있습니다. 수정 키는 이 브라우저에만 저장됩니다. 아이콘은 로그인한 뒤에만 올릴 수 있습니다.
              </div>
            )}

            {myGames.length > 0 && (
              <div style={{ background: 'white', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#161616' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                  <span style={{ fontWeight: 600 }}>필터</span>
                  <div style={{ flex: 1 }} />
                  <Button variant="outline" size="small" onClick={handleFilterReset} disabled={!hasActiveFilters}>
                    필터 초기화
                  </Button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>게임 제목 검색</label>
                    <input
                      value={titleFilter}
                      onChange={(e) => setTitleFilter(e.target.value)}
                      placeholder="제목 또는 설명으로 검색"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>학교</label>
                      <select
                        value={schoolFilter}
                        onChange={(e) => {
                          setSchoolFilter(e.target.value);
                          setGradeFilter('');
                          setClassFilter('');
                        }}
                        style={inputStyle}
                      >
                        <option value="">전체</option>
                        {filterSchools.map((school) => (
                          <option key={school} value={school}>{school}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>학년</label>
                      <select
                        value={gradeFilter}
                        onChange={(e) => {
                          setGradeFilter(e.target.value);
                          setClassFilter('');
                        }}
                        style={inputStyle}
                      >
                        <option value="">전체</option>
                        {filterGrades.map((g) => (
                          <option key={g} value={g}>{g}학년</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>반</label>
                      <select
                        value={classFilter}
                        onChange={(e) => setClassFilter(e.target.value)}
                        style={inputStyle}
                        disabled={!gradeFilter && filterClasses.length === 0}
                      >
                        <option value="">전체</option>
                        {filterClasses.map((c) => (
                          <option key={c} value={c}>{c}반</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>게임 유형</label>
                      <select
                        value={gameTypeFilter}
                        onChange={(e) => setGameTypeFilter(e.target.value as 'all' | 'evaluation' | 'regular')}
                        style={inputStyle}
                      >
                        <option value="all">전체</option>
                        <option value="evaluation">평가용</option>
                        <option value="regular">일반</option>
                      </select>
                    </div>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>정렬</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                        style={inputStyle}
                      >
                        <option value="createdAt">최신 등록순</option>
                        <option value="title">제목순</option>
                        <option value="rating">별점순</option>
                        <option value="playCount">플레이순</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {myGames.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#666', background: 'white', borderRadius: 12 }}>
                {isGuest ? '이 브라우저에서 코드로 등록한 게임이 여기에 나타납니다.' : '등록한 게임이 없습니다.'}
              </div>
            ) : filteredMyGames.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#666', background: 'white', borderRadius: 12 }}>
                조건에 맞는 게임이 없습니다.
              </div>
            ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, 243px)',
              gap: 16,
              justifyContent: 'flex-start',
            }}>
              {filteredMyGames.map((game) => (
                <div
                  key={game.id}
                  style={{
                    width: isMobile ? '100%' : 243,
                    background: 'white',
                    borderRadius: 12,
                    padding: 16,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    color: '#161616',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      borderRadius: 12,
                      overflow: 'hidden',
                      marginBottom: 12,
                    }}
                  >
                    <ArcadeGamePoster id={game.id} title={game.title} iconUrl={game.iconUrl} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: '#161616',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {game.title}
                      </div>
                      {game.isEvaluationGame && (
                        <div style={{ fontSize: 12, color: 'var(--Color-fill-brand-color, #FF6F0F)', marginTop: 2 }}>
                          평가용 게임
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    별점 {game.rating} · 플레이 {game.playCount}
                  </div>
                  {(game.grade || game.class) && (
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      {game.grade && game.class ? `${game.grade}학년 ${game.class}반` : game.grade ? `${game.grade}학년` : `${game.class}반`}
                    </div>
                  )}
                  {game.isEvaluationGame && game.evaluationInfo && (
                    <div style={{
                      fontSize: 12,
                      color: '#989898',
                      marginTop: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      평가 기간: {formatDate(game.evaluationInfo.startDate)} ~ {formatDate(game.evaluationInfo.endDate)}
                    </div>
                  )}
                  <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Button variant="outline" size="small" onClick={() => navigate(`/makecodearcade/game/${game.id}`)}>상세</Button>
                    {currentUser ? (
                      <>
                        <Button variant="outline" size="small" onClick={() => handleEdit(game)}>수정</Button>
                        <Button variant="cancel" size="small" onClick={() => handleDelete(game.id)}>삭제</Button>
                      </>
                    ) : (
                      <Button variant="outline" size="small" onClick={() => handleEdit(game)}>수정</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
            </>
            )}
          </>
      </div>
    </MainLayout>
  );
};

export default GameRegister;
